import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { PricePointRow, PriceTableVersionRow } from '../../api';
import { eventEndpoints } from '../../api';
import { Badge, Button, EmptyRow, Panel, Table } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, dollars, humanize, toCents } from './shared';
import { dateWords } from '../../terms';

/**
 * The price table. Prices are never edited in place: an active table is what
 * sales snapshot, so a change — a new ticket type, a new tier, a price bump —
 * is a new version that copies the current one, is edited, and is activated.
 * The panel makes that one click and shows where the gaps are, so "I added a
 * tier" is a matter of filling the empty cells and pressing Activate.
 */

const CHANNELS = ['WEB', 'BOX_OFFICE', 'PHONE', 'GROUP_SALES'];

const key = (p: { performance_id: string | null; pricing_tier_id: string | null; price_zone_id: string; ticket_type_id: string; channel: string | null }) =>
  [p.performance_id ?? '', p.pricing_tier_id ?? '', p.price_zone_id, p.ticket_type_id, p.channel ?? ''].join('|');

export function PricingPanel({ detail, refresh, onError, can }: PanelProps) {
  const editable = can('pricing.manage') && detail.event.status !== 'ARCHIVED';
  const versions = useQuery({ queryKey: ['price-tables', detail.event.id], queryFn: () => eventEndpoints.priceTables(detail.event.id) });
  const list: PriceTableVersionRow[] = versions.data?.versions ?? [];
  const draft = list.find((v) => v.status === 'DRAFT') ?? null;
  const active = list.find((v) => v.status === 'ACTIVE') ?? null;
  const [viewing, setViewing] = useState<string | null>(null);
  const shown = viewing ?? draft?.id ?? active?.id ?? null;

  const table = useQuery({ queryKey: ['price-table', detail.event.id, shown], queryFn: () => eventEndpoints.priceTable(detail.event.id, shown!), enabled: Boolean(shown) });

  const createDraft = useMutation({
    mutationFn: (reason: string | undefined) => eventEndpoints.draftPriceTable(detail.event.id, reason),
    onSuccess: (result) => {
      setViewing(result.version.id);
      void versions.refetch();
    },
    onError,
  });
  const activate = useMutation({
    mutationFn: (versionId: string) => eventEndpoints.activatePriceTable(detail.event.id, versionId),
    onSuccess: () => {
      setViewing(null);
      void versions.refetch();
      void table.refetch();
      refresh();
    },
    onError,
  });
  const discard = useMutation({
    mutationFn: (versionId: string) => eventEndpoints.discardPriceTable(detail.event.id, versionId),
    onSuccess: () => {
      setViewing(null);
      void versions.refetch();
      refresh();
    },
    onError,
  });

  const zones = detail.price_zones;
  const types = detail.ticket_types.filter((t) => t.active === 1);
  const ready = zones.length > 0 && types.length > 0;

  // What the active table is missing, so the operator sees "add prices" before a customer sees "no price".
  const activePoints = shown === active?.id ? (table.data?.points ?? []) : [];
  const missingInActive = useMemo(() => {
    if (!active || shown !== active.id) return [];
    const have = new Set(activePoints.filter((p) => !p.performance_id && !p.channel).map((p) => `${p.pricing_tier_id ?? ''}|${p.price_zone_id}|${p.ticket_type_id}`));
    const missing: string[] = [];
    for (const zone of zones) for (const type of types) if (!have.has(`|${zone.id}|${type.id}`)) missing.push(`${zone.name} · ${type.name}`);
    return missing;
  }, [active, shown, activePoints, zones, types]);

  return (
    <div className="space-y-4">
      <Panel
        title="Price tables"
        description="Every price lives in a version. Sales snapshot the version they were priced under, so changing prices — or adding a ticket type or tier — means editing a copy and activating it."
        actions={
          editable && !draft && ready ? (
            <Button variant="primary" onClick={() => createDraft.mutate(active ? 'Edited prices' : undefined)} disabled={createDraft.isPending}>
              {active ? 'Edit prices (new version)' : 'Start the price table'}
            </Button>
          ) : null
        }
      >
        {!ready ? (
          <p className="px-5 py-4 text-sm text-ink-600">Add at least one ticket type and one price zone first; prices are the grid of the two.</p>
        ) : (
          <>
            {missingInActive.length > 0 && !draft ? (
              <p className="mx-5 mt-4 rounded border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                The active table has no price for {missingInActive.length === 1 ? missingInActive[0] : `${missingInActive.length} zone × ticket-type combinations`}
                {missingInActive.length > 1 ? ` (${missingInActive.slice(0, 3).join(', ')}${missingInActive.length > 3 ? '…' : ''})` : ''}. Those cannot be sold until a new version fills them in.
              </p>
            ) : null}
            <Table headers={['Version', 'Status', 'Prices', 'Reason', 'Since', '']}>
              {list.length === 0 ? (
                <EmptyRow columns={6}>No price table yet.</EmptyRow>
              ) : (
                list.map((v) => (
                  <tr key={v.id} className={`border-b border-line last:border-0 ${shown === v.id ? 'bg-page/60' : ''}`}>
                    <td className="tabular px-5 py-3 font-medium text-ink-900">v{v.version}</td>
                    <td className="px-5 py-3">
                      <Badge tone={v.status === 'ACTIVE' ? 'ok' : v.status === 'DRAFT' ? 'accent' : 'neutral'}>{v.status}</Badge>
                    </td>
                    <td className="tabular px-5 py-3 text-sm text-ink-600">{v.price_points ?? 0}</td>
                    <td className="px-5 py-3 text-sm text-ink-600">{v.reason ?? '—'}</td>
                    <td className="px-5 py-3 text-xs text-ink-400">{v.effective_from ?? '—'}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Button onClick={() => setViewing(v.id)}>{shown === v.id ? 'Showing' : 'Show'}</Button>
                      {editable && v.status === 'DRAFT' ? (
                        <>
                          {can('pricing.activate') ? (
                            <Button variant="primary" className="ml-2" onClick={() => activate.mutate(v.id)} disabled={activate.isPending || (v.price_points ?? 0) === 0}>
                              Activate
                            </Button>
                          ) : null}
                          <Button
                            variant="danger"
                            className="ml-2"
                            onClick={() => {
                              if (window.confirm('Discard this draft? The active table is untouched.')) discard.mutate(v.id);
                            }}
                            disabled={discard.isPending}
                          >
                            Discard
                          </Button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </Table>
          </>
        )}
      </Panel>

      {shown && table.data ? (
        <PriceGrid
          detail={detail}
          version={table.data.version}
          points={table.data.points}
          editable={editable && table.data.version.status === 'DRAFT'}
          onSaved={() => {
            void table.refetch();
            void versions.refetch();
          }}
          onError={onError}
        />
      ) : null}
    </div>
  );
}

function PriceGrid({ detail, version, points, editable, onSaved, onError }: { detail: PanelProps['detail']; version: PriceTableVersionRow; points: PricePointRow[]; editable: boolean; onSaved: () => void; onError: PanelProps['onError'] }) {
  const zones = detail.price_zones;
  const types = detail.ticket_types.filter((t) => t.active === 1);
  const tiers = detail.pricing_tiers;
  const [cells, setCells] = useState<Map<string, PricePointRow>>(new Map());
  const [tierTab, setTierTab] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [extra, setExtra] = useState<{ zone: string; type: string; channel: string; tier: string; performance: string; face: string; list: string }>({ zone: zones[0]?.id ?? '', type: types[0]?.id ?? '', channel: '', tier: '', performance: '', face: '', list: '' });

  useEffect(() => {
    setCells(new Map(points.map((p) => [key(p), { ...p }])));
    setDirty(false);
  }, [points]);

  const cellKey = (zoneId: string, typeId: string) => key({ performance_id: null, pricing_tier_id: tierTab || null, price_zone_id: zoneId, ticket_type_id: typeId, channel: null });
  const cell = (zoneId: string, typeId: string) => cells.get(cellKey(zoneId, typeId));
  const setCell = (zoneId: string, typeId: string, value: string) => {
    const cents = toCents(value);
    setCells((current) => {
      const next = new Map(current);
      const k = cellKey(zoneId, typeId);
      const existing = next.get(k);
      if (cents === null) next.delete(k);
      else if (existing && existing.face_value_cents === cents) return current;
      else next.set(k, { performance_id: null, pricing_tier_id: tierTab || null, price_zone_id: zoneId, ticket_type_id: typeId, channel: null, face_value_cents: cents, list_price_cents: existing && existing.list_price_cents < existing.face_value_cents ? Math.min(existing.list_price_cents, cents) : cents, active: 1 });
      setDirty(true);
      return next;
    });
  };
  const specific = useMemo(() => [...cells.values()].filter((p) => p.performance_id || p.channel), [cells]);
  const hasBase = (zoneId: string, typeId: string) => cells.has(key({ performance_id: null, pricing_tier_id: null, price_zone_id: zoneId, ticket_type_id: typeId, channel: null }));
  // A blank cell means "this ticket type is not sold in this zone" — normal for table seats
  // vs general admission. Only a zone with no price at all, or a type priced nowhere, is a problem.
  const zonesUnsellable = zones.filter((z) => !types.some((t) => hasBase(z.id, t.id))).map((z) => z.name);
  const typesUnsellable = types.filter((t) => !zones.some((z) => hasBase(z.id, t.id))).map((t) => t.name);

  const save = useMutation({
    mutationFn: () =>
      eventEndpoints.setPricePoints(
          detail.event.id,
          version.id,
          [...cells.values()].map(({ id: _id, ...point }) => ({ ...point, active: Boolean(point.active) })),
        ),
    onSuccess: () => {
      setDirty(false);
      onSaved();
    },
    onError,
  });

  const addSpecific = () => {
    const face = toCents(extra.face);
    if (face === null) return;
    const list = toCents(extra.list) ?? face;
    const point: PricePointRow = { performance_id: extra.performance || null, pricing_tier_id: extra.tier || null, price_zone_id: extra.zone, ticket_type_id: extra.type, channel: extra.channel || null, face_value_cents: face, list_price_cents: list, active: 1 };
    setCells((current) => new Map(current).set(key(point), point));
    setDirty(true);
    setExtra({ ...extra, face: '', list: '' });
  };

  const name = (id: string, rows: { id: string; name?: string; code?: string }[]) => rows.find((r) => r.id === id)?.name ?? rows.find((r) => r.id === id)?.code ?? id;
  const tierName = tierTab ? name(tierTab, tiers) : 'Base';

  return (
    <Panel
      title={`Price table v${version.version} (${version.status.toLowerCase()})`}
      description={
        editable
          ? `Base prices apply on every channel, tier and ${dateWords().one}. A tier tab prices that tier; empty cells there fall back to the base price. An empty base cell means that ticket type is not sold in that zone.`
          : 'Locked: this version is what sales were priced under. Press "Edit prices" above to change it in a new version.'
      }
    >
      <div className="space-y-4 px-5 py-4">
        {tiers.length > 0 ? (
          <div className="flex flex-wrap gap-1 border-b border-line" role="tablist" aria-label="Pricing tier">
            {[{ id: '', name: 'Base' }, ...tiers].map((tier) => (
              <button
                key={tier.id}
                type="button"
                role="tab"
                aria-selected={tierTab === tier.id}
                onClick={() => setTierTab(tier.id)}
                className={`-mb-px rounded-t border-b-2 px-3 py-1.5 text-sm ${tierTab === tier.id ? 'border-accent font-medium text-ink-900' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
              >
                {tier.name}
              </button>
            ))}
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left text-xs font-semibold tracking-wide text-ink-400 uppercase">{tierName} · zone</th>
                {types.map((t) => (
                  <th key={t.id} className="px-2 py-1 text-left text-xs font-semibold tracking-wide text-ink-400 uppercase">
                    {t.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id}>
                  <td className="px-2 py-1 font-medium text-ink-900">
                    <span className="mr-2 inline-block h-3 w-3 rounded-sm align-middle" style={{ background: z.color_hex ?? '#d9d9d9' }} />
                    {z.name}
                  </td>
                  {types.map((t) => {
                    const c = cell(z.id, t.id);
                    const base = tierTab ? cells.get(key({ performance_id: null, pricing_tier_id: null, price_zone_id: z.id, ticket_type_id: t.id, channel: null })) : undefined;
                    const blank = !c && !base;
                    return (
                      <td key={t.id} className="px-2 py-1">
                        {editable ? (
                          <input
                            key={`${tierTab}-${z.id}-${t.id}-${c?.face_value_cents ?? 'none'}`}
                            aria-label={`${tierName} ${z.name} ${t.name} price`}
                            defaultValue={c ? (c.face_value_cents / 100).toFixed(2) : ''}
                            onBlur={(e) => setCell(z.id, t.id, e.target.value)}
                            placeholder={base ? `(${dollars(base.face_value_cents)})` : 'not sold'}
                            title={blank ? `${t.name} is not sold in ${z.name}. Enter a price to sell it there.` : undefined}
                            inputMode="decimal"
                            className={`tabular w-24 ${INPUT} ${blank ? 'text-ink-400' : ''}`}
                          />
                        ) : (
                          <span className={`tabular ${blank ? 'text-ink-400' : ''}`}>{c ? dollars(c.face_value_cents) : base ? `(${dollars(base.face_value_cents)})` : 'not sold'}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-400">Leave a cell empty when that ticket type is not sold in that zone — a table seat priced only under Table Seat, general admission only under GA. Empty cells are never offered to a buyer.</p>
        {zonesUnsellable.length > 0 || typesUnsellable.length > 0 ? (
          <p className="text-xs text-warn">
            {zonesUnsellable.length > 0 ? `Nothing can be sold in ${zonesUnsellable.join(', ')}: no ticket type has a price there. ` : ''}
            {typesUnsellable.length > 0 ? `${typesUnsellable.join(', ')} ${typesUnsellable.length === 1 ? 'is' : 'are'} priced in no zone, so ${typesUnsellable.length === 1 ? 'it' : 'they'} cannot be sold at all.` : ''}
          </p>
        ) : null}

        <div>
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Channel- and {dateWords().one}-specific prices</p>
          {specific.length === 0 ? (
            <p className="mt-1 text-sm text-ink-400">None. The grid prices apply on every channel and {dateWords().one}.</p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {specific.map((p) => (
                <li key={key(p)} className="flex items-center gap-2">
                  <span>
                    {name(p.price_zone_id, zones)} · {name(p.ticket_type_id, types)}
                    {p.channel ? ` · ${humanize(p.channel)}` : ''}
                    {p.pricing_tier_id ? ` · ${name(p.pricing_tier_id, tiers)}` : ''}
                    {p.performance_id ? ` · ${detail.performances.find((x) => x.id === p.performance_id)?.code ?? p.performance_id}` : ''}:
                  </span>
                  <span className="tabular font-medium">{dollars(p.face_value_cents)}</span>
                  {p.list_price_cents !== p.face_value_cents ? <span className="tabular text-ink-400">(offered at {dollars(p.list_price_cents)})</span> : null}
                  {editable ? (
                    <button type="button" className="text-ink-400 hover:text-bad" aria-label="Remove price" onClick={() => { setCells((c) => { const n = new Map(c); n.delete(key(p)); return n; }); setDirty(true); }}>
                      ×
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {editable ? (
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <select value={extra.zone} onChange={(e) => setExtra({ ...extra, zone: e.target.value })} className={INPUT} aria-label="Zone">
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
              <select value={extra.type} onChange={(e) => setExtra({ ...extra, type: e.target.value })} className={INPUT} aria-label="Ticket type">
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <select value={extra.channel} onChange={(e) => setExtra({ ...extra, channel: e.target.value })} className={INPUT} aria-label="Channel">
                <option value="">any channel</option>
                {CHANNELS.map((ch) => (
                  <option key={ch} value={ch}>
                    {humanize(ch)}
                  </option>
                ))}
              </select>
              <select value={extra.tier} onChange={(e) => setExtra({ ...extra, tier: e.target.value })} className={INPUT} aria-label="Tier">
                <option value="">any tier</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <select value={extra.performance} onChange={(e) => setExtra({ ...extra, performance: e.target.value })} className={INPUT} aria-label={`${dateWords().One}`}>
                <option value="">any {dateWords().one}</option>
                {detail.performances.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code}
                  </option>
                ))}
              </select>
              <input value={extra.face} onChange={(e) => setExtra({ ...extra, face: e.target.value })} placeholder="face $" inputMode="decimal" className={`tabular w-24 ${INPUT}`} aria-label="Face value" />
              <input value={extra.list} onChange={(e) => setExtra({ ...extra, list: e.target.value })} placeholder="offer $ (opt.)" inputMode="decimal" className={`tabular w-28 ${INPUT}`} aria-label="List price" />
              <Button onClick={addSpecific} disabled={toCents(extra.face) === null}>
                Add
              </Button>
            </div>
          ) : null}
        </div>

        {editable ? (
          <div className="flex items-center gap-3">
            <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : `Save ${cells.size} price(s)`}
            </Button>
            {dirty ? <span className="text-xs text-warn">Unsaved changes</span> : <span className="text-xs text-ink-400">Saved. Activate the version above when it is ready.</span>}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
